import type { LegendEntry, PrefabData } from "./prefabData";
import { getThingClass } from "../../serialization";
import { SimpleBoard } from "../simpleBoard";
import { XY } from "../../xy";
import { Prefab } from "../prefab";

export function buildPrefab(data: PrefabData): Prefab {
	// Validate: must have exactly one of layout or layers
	const hasLayout = data.layout !== undefined;
	const hasLayers = data.layers !== undefined;
	if (hasLayout && hasLayers) {
		throw new Error("PrefabData must have exactly one of 'layout' or 'layers', found both.");
	}
	if (!hasLayout && !hasLayers) {
		throw new Error("PrefabData must have exactly one of 'layout' or 'layers', found neither.");
	}

	// Validate legend is non-empty
	if (!data.legend || Object.keys(data.legend).length === 0) {
		throw new Error("PrefabData must have a non-empty 'legend'.");
	}

	// Normalize to layers
	const layers: string[][] = hasLayout ? [data.layout!] : data.layers!;

	// Validate dimensions
	if (layers.length === 0) {
		throw new Error("PrefabData layers must not be empty.");
	}
	const expectedHeight = layers[0].length;
	const expectedWidth = expectedHeight > 0 ? layers[0][0].length : 0;

	for (let layerIdx = 0; layerIdx < layers.length; layerIdx++) {
		const layer = layers[layerIdx];
		if (layer.length !== expectedHeight) {
			throw new Error(`Layer ${layerIdx} has ${layer.length} rows, expected ${expectedHeight}.`);
		}
		for (let rowIdx = 0; rowIdx < layer.length; rowIdx++) {
			if (layer[rowIdx].length !== expectedWidth) {
				throw new Error(
					`Layer ${layerIdx}, row ${rowIdx} has length ${layer[rowIdx].length}, expected ${expectedWidth}.`,
				);
			}
		}
	}

	// Validate legend references
	const errors: string[] = [];

	// Check all non-space characters exist in legend
	for (let layerIdx = 0; layerIdx < layers.length; layerIdx++) {
		const layer = layers[layerIdx];
		for (let rowIdx = 0; rowIdx < layer.length; rowIdx++) {
			for (let colIdx = 0; colIdx < layer[rowIdx].length; colIdx++) {
				const char = layer[rowIdx][colIdx];
				if (char !== " " && !(char in data.legend)) {
					errors.push(`Unknown character '${char}' at layer ${layerIdx}, row ${rowIdx}, col ${colIdx}.`);
				}
			}
		}
	}

	// Check all legend class names exist in registry
	for (const [key, entry] of Object.entries(data.legend)) {
		const className = typeof entry === "string" ? entry : entry.class;
		if (!getThingClass(className)) {
			errors.push(`Unknown class '${className}' in legend key '${key}'.`);
		}
	}

	if (errors.length > 0) {
		throw new Error("PrefabData validation errors:\n" + errors.join("\n"));
	}

	// Build the prefab
	const board = new SimpleBoard();

	for (let layerIdx = 0; layerIdx < layers.length; layerIdx++) {
		const layer = layers[layerIdx];
		for (let y = 0; y < layer.length; y++) {
			for (let x = 0; x < layer[y].length; x++) {
				const char = layer[y][x];
				if (char === " " && !(char in data.legend)) continue;

				const entry = data.legend[char];
				let className: string;
				let props: Record<string, unknown> | undefined;

				if (typeof entry === "string") {
					className = entry;
					props = undefined;
				} else {
					className = entry.class;
					props = entry.props;
				}

				const EntityCls = getThingClass(className)!;
				EntityCls.initOnBoard(new XY(x, y), board, props);
			}
		}
	}

	const prefab = new Prefab(board);
	prefab.cornerRequired = data.cornerRequired ?? false;
	prefab.borderRequired = data.borderRequired ?? false;
	return prefab;
}

export function buildPrefabWithOverrides(
	data: PrefabData,
	legendOverrides: Record<string, string | LegendEntry>,
): Prefab {
	const cloned: PrefabData = {
		...data,
		legend: { ...data.legend, ...legendOverrides },
	};
	return buildPrefab(cloned);
}
